import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Save, Settings } from "lucide-react";
import { toast } from "sonner";

const settingLabels = {
  platform_name: "Platform Name",
  suspension_message: "Suspension Page Message",
  homepage_cta: "Homepage CTA Button Text",
  homepage_subtitle: "Homepage Subtitle Text",
  default_status: "Default Account Status",
  default_trial_days: "Default Trial Days",
};

const textareaKeys = ["suspension_message", "homepage_subtitle"];

export default function AdminSettings() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((res) => {
      setSettings(res.data || {});
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleChange = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { settings });
      toast.success("Settings saved");
    } catch {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in space-y-8 max-w-2xl">
      <div>
        <h1 className="font-heading text-2xl font-bold mb-1">Platform Settings</h1>
        <p className="text-muted-foreground text-sm">Configure platform-wide settings</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-base font-semibold flex items-center gap-2">
            <Settings size={16} className="text-amber-400" /> General Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {Object.entries(settingLabels).map(([key, label]) => (
            <div key={key} className="space-y-2">
              <Label htmlFor={key}>{label}</Label>
              {textareaKeys.includes(key) ? (
                <Textarea
                  id={key}
                  value={settings[key] || ""}
                  onChange={(e) => handleChange(key, e.target.value)}
                  rows={3}
                  data-testid={`setting-${key}`}
                />
              ) : (
                <Input
                  id={key}
                  value={settings[key] || ""}
                  onChange={(e) => handleChange(key, e.target.value)}
                  data-testid={`setting-${key}`}
                />
              )}
            </div>
          ))}

          <Button onClick={handleSave} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="save-settings-btn">
            <Save size={16} className="mr-2" />
            {saving ? "Saving..." : "Save Settings"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="py-4">
          <p className="text-xs text-muted-foreground">
            These settings control platform behavior. Changes to homepage text and suspension messages will take effect when the frontend is configured to read from this API.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
